<template>
  <div class="devices-container">
    <!-- Header Section -->
    <div class="table_card">
      <div class="table_card__head">
        <v-layout row wrap fill-height>
          <v-flex xs12 lg8>
            <p class="table_card__head--title">Dispositivos</p>
            <v-breadcrumbs class="breadcums">
              <v-icon slot="divider">chevron_right</v-icon>
              <v-breadcrumbs-item class="breadcums__option">
                Listado de dispositivos
              </v-breadcrumbs-item>
            </v-breadcrumbs>
          </v-flex>
          <v-flex xs12 lg4 class="filter_options">
            <v-flex class="right mr-20">
              <button class="faccess_button" @click="navigateToNewDevice" :disabled="isLoading">
                Crear nuevo dispositivo
              </button>
            </v-flex>
            <div class="buttom_reload" @click="getDevices()" :class="{ 'loading': isLoading }">
              <img src="@/assets/img/icons/refresh-icon.svg" alt="Recargar" />
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- Registered Devices Table -->
      <div class="devices-section">
        <v-data-table
          :headers="headers"
          :items="devices"
          :loading="isLoading"
          hide-actions
          class="elevation-1"
        >
          <template slot="items" slot-scope="props">
            <td class="text-xs-center">
              <a class="device-link" @click="editItem(props.item.id)">
                {{ props.item.name }}
              </a>
            </td>
            <td class="text-xs-center">{{ props.item.type || 'N/A' }}</td>
            <td class="text-xs-center">{{ props.item.location || 'N/A' }}</td>
            <td class="text-xs-center">{{ getDeviceUuid(props.item) }}</td>
            <td class="text-xs-center">
              <a class="action-link" @click.prevent="openConfirmModal(props.item)">
                Desvincular
              </a>
            </td>
          </template>
          <template slot="no-data">
            <div class="text-xs-center pa-3">
              No hay dispositivos registrados
            </div>
          </template>
        </v-data-table>

        <!-- Pagination -->
        <div class="table_card__footer justify-content-start">
          <nav class="pagination" role="navigation" aria-label="pagination">
            <v-pagination v-model="page" :length="pages" :total-visible="7" @input="changePage" />
          </nav>
        </div>
      </div>
    </div>

    <!-- Available Devices Section -->
    <div class="table_card available_devices mt-5">
      <div class="table_card__head">
        <v-layout row wrap align-center>
          <v-flex xs12 md8>
            <p class="table_card__head--title">Dispositivos Disponibles</p>
          </v-flex>
          <v-flex xs12 md4 class="text-xs-right">
            <v-btn
              color="primary"
              @click="getAvailableDevices"
              :loading="isLoadingAvailable"
              :disabled="isLoadingAvailable"
            >
              Cargar dispositivos
            </v-btn>
          </v-flex>
        </v-layout>
      </div>

      <div v-if="availableDevices && hasAvailableDevices" class="available-devices-content">
        <!-- Cameras Table -->
        <div v-if="availableDevices.cameras.length > 0" class="device-type-section">
          <h4 class="device-type-title">Cámaras ({{ availableDevices.cameras.length }})</h4>
          <v-data-table
            :headers="availableHeaders"
            :items="availableDevices.cameras"
            class="elevation-1"
            hide-actions
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-center">{{ props.item.id }}</td>
              <td class="text-xs-center">{{ props.item.name || 'Sin nombre' }}</td>
              <td class="text-xs-center">{{ props.item.access_devcode || 'N/A' }}</td>
              <td class="text-xs-center">
                <span :class="getStatusClass(props.item.status)">
                  {{ props.item.status }}
                </span>
              </td>
            </template>
          </v-data-table>
        </div>

        <!-- Doors Table -->
        <div v-if="availableDevices.doors.length > 0" class="device-type-section">
          <h4 class="device-type-title">Puertas ({{ availableDevices.doors.length }})</h4>
          <v-data-table
            :headers="availableHeaders"
            :items="availableDevices.doors"
            class="elevation-1"
            hide-actions
          >
            <template slot="items" slot-scope="props">
              <td class="text-xs-center">{{ props.item.id }}</td>
              <td class="text-xs-center">{{ props.item.name || 'Sin nombre' }}</td>
              <td class="text-xs-center">{{ props.item.access_devcode || 'N/A' }}</td>
              <td class="text-xs-center">
                <span :class="getStatusClass(props.item.status)">
                  {{ props.item.status }}
                </span>
              </td>
            </template>
          </v-data-table>
        </div>
      </div>

      <div v-else-if="!isLoadingAvailable" class="text-xs-center pa-5">
        <p>No hay dispositivos disponibles para cargar</p>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <section v-if="showModal" class="access_devices__modal">
      <section class="access_devices__modal--container" style="width: 40%;">
        <p class="access_devices__modal--text" style="text-align: justify;">
          <b>AVISO:</b> Al desvincular el dispositivo se <b>eliminará</b> su UUID y pasará a estar inactivo.
        </p>
        <br>
        <p class="access_devices__modal--text">
          ¿Está seguro de desvincular el dispositivo <b>{{ selectedDevice.name }}</b>?
        </p>
        <div class="access_devices__modal--buttons" style="margin-top: 1rem;">
          <div class="access_devices__modal--button" @click="closeConfirmModal" :disabled="isProcessing">
            Cancelar
          </div>
          <div class="access_devices__modal--button active" @click="removeDevice" :disabled="isProcessing">
            {{ isProcessing ? 'Procesando...' : 'Sí' }}
          </div>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
export default {
  name: 'DevicesList',

  data() {
    return {
      // Loading states
      isLoading: false,
      isProcessing: false,
      isLoadingAvailable: false,

      // Device data
      devices: [],
      selectedDevice: {},
      availableDevices: {
        doors: [],
        cameras: [],
      },

      // Modal state
      showModal: false,

      // Pagination
      page: 1,
      pages: 1,

      // Table headers
      headers: [
        { text: "Nombre", sortable: false, value: "name", align: "center" },
        { text: "Tipo", sortable: false, value: "type", align: "center" },
        { text: "Locación", sortable: false, value: "location", align: "center" },
        { text: "UUID", sortable: false, value: "uuid", align: "center" },
        { text: "Desvincular", value: "name", sortable: false, align: "center" }
      ],
      availableHeaders: [
        { text: "ID", value: "id", sortable: false, align: "center" },
        { text: "Nombre", value: "name", sortable: false, align: "center" },
        { text: "DevCode", value: "access_devcode", sortable: false, align: "center" },
        { text: "Status", value: "status", sortable: false, align: "center" }
      ]
    };
  },

  computed: {
    hasAvailableDevices() {
      return this.availableDevices.cameras.length > 0 || this.availableDevices.doors.length > 0;
    }
  },

  mounted() {
    this.getDevices();
  },

  methods: {
    // Navigation methods
    navigateToNewDevice() {
      this.$router.push({ name: 'new_device' });
    },

    editItem(id) {
      if (!id) {
        console.error('ID de dispositivo no válido');
        return;
      }
      this.$router.push({ name: "edit_device", params: { id } });
    },

    // Modal methods
    openConfirmModal(device) {
      this.selectedDevice = device;
      this.showModal = true;
    },

    closeConfirmModal() {
      this.selectedDevice = {};
      this.showModal = false;
    },

    // Data fetching methods
    async getDevices() {
      this.isLoading = true;
      const params = { page: this.page };

      try {
        const response = await this.$http.get("scan_peripheral/get_devices", {
          headers: {
            "X-Device-ID": this.getDeviceId(),
            Authorization: "Bearer " + this.getUserToken()
          },
          params
        });

        if (response.body) {
          this.devices = response.body.devices || [];
          this.pages = response.body.pages || 1;
        }

        this.handleResponse(response);

      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async getAvailableDevices() {
      const company = this.getCompany();

      if (!company || !company.id) {
        this.handleError('No se pudo obtener la información de la compañía');
        return;
      }

      this.isLoadingAvailable = true;
      const payload = { company_id: company.id };

      try {
        const response = await this.$http.post(
          "scan_peripheral/get_available_devices",
          payload,
          {
            headers: {
              "X-Device-ID": this.getDeviceId(),
              Authorization: "Bearer " + this.getUserToken()
            }
          }
        );

        if (response.body && response.body.success) {
          this.availableDevices = response.body.data;
          this.updateAlertMessage('Dispositivos disponibles cargados exitosamente');
          this.updateIsInAlert(true);
        } else {
          this.handleError('No se pudieron cargar los dispositivos disponibles');
        }

      } catch (error) {
        this.handleError(error, 'Error obteniendo dispositivos disponibles');
      } finally {
        this.isLoadingAvailable = false;
      }
    },

    async removeDevice() {
      if (!this.selectedDevice || !this.selectedDevice.id) {
        this.handleError('No se ha seleccionado ningún dispositivo');
        return;
      }

      this.isProcessing = true;
      const data = { device_info: this.selectedDevice };

      try {
        const response = await this.$http.post(
          "scan_peripheral/remove_peripheral",
          { data: this.encrypt(data).toString() },
          {
            headers: {
              "X-Device-ID": this.getDeviceId(),
              Authorization: "Bearer " + this.getUserToken()
            }
          }
        );

        this.handleResponse(response);
        this.closeConfirmModal();
        await this.getDevices();

      } catch (error) {
        this.handleError(error);
      } finally {
        this.isProcessing = false;
      }
    },

    // Utility methods
    changePage(page) {
      this.page = page;
      this.getDevices();
    },

    getDeviceUuid(device) {
      if (!device.uuid && device.name) {
        return device.name.split('-')[0].trim();
      }
      return device.uuid || 'N/A';
    },

    getStatusClass(status) {
      return {
        'status-online': status === 'online',
        'status-offline': status === 'offline'
      };
    },

    // Response handlers
    handleResponse(response) {
      if (response.body) {
        if (response.body.ans) {
          this.updateAlertMessage(response.body.ans);
          this.updateIsInAlert(true);
        }

        if (response.body.meta && response.body.meta.authentication_token) {
          this.checkToken(response.body.meta.authentication_token);
        }
      }
    },

    handleError(error, customMessage) {
      console.error('Error:', error);

      const message = customMessage ||
        (error.body && error.body.ans) ||
        error.message ||
        'Ha ocurrido un error inesperado';

      this.updateAlertMessage(message);
      this.updateIsInAlert(true);

      if (error.body && error.body.meta && error.body.meta.authentication_token) {
        this.checkToken(error.body.meta.authentication_token);
      }
    }
  }
};
</script>

<style scoped>
.devices-container {
  padding: 0;
}

.devices-section {
  margin-bottom: 2rem;
}

.available_devices {
  margin-top: 2rem;
}

.available-devices-content {
  padding: 1.5rem;
}

.device-type-section {
  margin-bottom: 2rem;
}

.device-type-section:last-child {
  margin-bottom: 0;
}

.device-type-title {
  margin-bottom: 1rem;
  padding: 0.5rem 0;
  border-bottom: 2px solid #e0e0e0;
  color: #424242;
  font-weight: 600;
}

.device-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;
}

.device-link:hover {
  text-decoration: underline;
}

.action-link {
  color: #d32f2f;
  cursor: pointer;
  text-decoration: none;
  font-weight: 500;
}

.action-link:hover {
  text-decoration: underline;
}

.status-online {
  color: #4caf50;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.status-offline {
  color: #f44336;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.buttom_reload.loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>



// WEBPACK FOOTER //
// src/components/views/devices/Index.vue